import { Theme } from '@emotion/react';
import { LoadingButton } from '@mui/lab';
import { Grid, SxProps, useMediaQuery } from '@mui/material';
import { useSnackbar } from 'notistack';
import { FC, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Loader, MobileTable, Modal, Table } from '../../../components';
import { getDocumentFileVersions, downloadDocumentFileVersion } from '../../../fetch';
import { downloadFile, formatDate, formatShortFriendlyTimeInCentral } from '../../../helpers';
import { IDocumentFileVersion } from '../../../models';

interface IFileManagementHistoryModal {
  documentId: number | null;
  documentName?: string;
  onClose: () => void;
  fileNames?: string[];
  titleClassName?: string;
  titleStyles?: SxProps<Theme>;
  dividerStyles?: SxProps<Theme>;
}

export const FileManagementHistoryModal: FC<IFileManagementHistoryModal> = ({
  documentId,
  documentName,
  onClose,
  fileNames
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [downloading, setDownloading] = useState<Record<number, boolean>>({});
  const isMobile = useMediaQuery('(max-width: 960px)');

  const handleDownload = async (documentVersionId: string | number, documentName: string) => {
    setDownloading(prev => ({ ...prev, [documentVersionId]: true }));
    try {
      const res = await downloadDocumentFileVersion(documentId, documentVersionId);
      downloadFile(res?.data, documentName);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error downloading document, please try again.`, {
        variant: 'error'
      });
    } finally {
      setDownloading(prev => ({ ...prev, [documentVersionId]: false }));
    }
  };

  const { isLoading, data: documentVersions } = useQuery(
    ['fileHistory', documentId],
    () => getDocumentFileVersions(documentId),
    {
      enabled: !!documentId
    }
  );

  const handleClose = () => {
    onClose();
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'File Name',
        accessor: 'fileName',
        sort: false,
        Cell: ({
          cell: {
            row: { original, index }
          }
        }: {
          cell: { row: { original: IDocumentFileVersion; index: number } };
        }) => {
          const displayName = original?.fileName;
          return (
            <LoadingButton
              sx={{ textDecoration: 'underline', padding: 0 }}
              variant="text"
              loading={downloading[original.fileManagementDocumentVersionId]}
              onClick={() => handleDownload(original.fileManagementDocumentVersionId, displayName)}
            >
              {displayName}
            </LoadingButton>
          );
        }
      },
      {
        Header: 'Date',
        accessor: 'uploadedDate',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IDocumentFileVersion } };
        }) => {
          const formattedDate = formatDate(original?.uploadedDate);
          return <div>{formattedDate}</div>;
        }
      },
      {
        Header: 'Time',
        accessor: 'uploadedTime',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IDocumentFileVersion } };
        }) => {
          const formattedTime = formatShortFriendlyTimeInCentral(original?.uploadedDate);
          return <div>{formattedTime}</div>;
        }
      }
    ]; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, documentVersions, downloading, fileNames]);

  return (
    <Modal
      open={!!documentId}
      title={documentName ? `${documentName} File History` : 'File History'}
      maxWidth="md"
      onClose={handleClose}
      titleMarginBottom={0.5}
    >
      {isLoading ? (
        <Grid container justifyContent="center">
          <Grid item>
            <Loader type="overlay" position="centered">
              Loading...
            </Loader>
          </Grid>
        </Grid>
      ) : (
        <Table
          columns={columns}
          data={documentVersions ?? []}
          isLoading={isLoading}
          noResultsText="No file history found"
          hidePagination
          mobileProps={{
            fields: columns
              .filter(c => c.Header)
              .map(c => ({
                name: c.Header,
                accessor: c.accessor,
                Cell: c?.Cell
              }))
          }}
          ResponsiveComponent={isMobile ? MobileTable : undefined}
        />
      )}
    </Modal>
  );
};
